import React, { useEffect, useState } from "react";
import Modal from "../../../shared/components/Modal";
import Loader from "../../../shared/components/Loader";
import { RichTextEditor } from "../../../shared/components/RichTextEditor/RichTextEditor";
import JobOpportunityClient, { JobOpportunity } from "../data/api/JobOpportunityClient";
import "../JobOpportunity.css";

export interface JobOpportunityFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  opportunity?: JobOpportunity | null;
}

const HiringTypes = ["Internal", "Sub contractor"];
const JobTypes = ["Full time", "Part time", "Contractor"];
const PaymentTypes = ["Annual", "Hourly"];
const WorkTypes = ["Remote", "Hybrid", "On-site"];
const Statuses = ["Draft", "Open", "Closed"];

export const JobOpportunityFormModal: React.FC<JobOpportunityFormModalProps> = ({ open, onClose, onSuccess, opportunity }) => {
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [forOrganizationName, setForOrganizationName] = useState("");
  const [applicationDeadline, setApplicationDeadline] = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [hiringType, setHiringType] = useState<string>("Internal");
  const [jobType, setJobType] = useState<string>("Full time");
  const [minYearsExperience, setMinYearsExperience] = useState<number>(0);
  const [paymentType, setPaymentType] = useState<string>("Annual");
  const [minCtc, setMinCtc] = useState<number | null>(null);
  const [maxCtc, setMaxCtc] = useState<number | null>(null);
  const [minHourlyRate, setMinHourlyRate] = useState<number | null>(null);
  const [maxHourlyRate, setMaxHourlyRate] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [skillsRequired, setSkillsRequired] = useState("");
  const [workType, setWorkType] = useState<string>("On-site");
  const [status, setStatus] = useState<string>("Draft");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditing = Boolean(opportunity?.opportunity_id);

  useEffect(() => {
    if (open) {
      if (opportunity) {
        setTitle(opportunity.title || "");
        setDepartment(opportunity.department || "");
        setForOrganizationName(opportunity.for_organization_name || "");
        setApplicationDeadline(toDateInput(opportunity.application_deadline));
        setWorkLocation(opportunity.work_location || "");
        setHiringType(opportunity.hiring_type || "Internal");
        setJobType(opportunity.job_type || "Full time");
        setMinYearsExperience(opportunity.min_years_experience ?? 0);
        setPaymentType(opportunity.payment_type || "Annual");
        setMinCtc(opportunity.min_ctc ?? null);
        setMaxCtc(opportunity.max_ctc ?? null);
        setMinHourlyRate(opportunity.min_hourly_rate ?? null);
        setMaxHourlyRate(opportunity.max_hourly_rate ?? null);
        setDescription(opportunity.description || "");
        setSkillsRequired((opportunity.skills_required || []).join(", "));
        setWorkType(opportunity.work_type || "On-site");
        setStatus(opportunity.status || "Draft");
      } else {
        setTitle("");
        setDepartment("");
        setForOrganizationName("");
        setApplicationDeadline("");
        setWorkLocation("");
        setHiringType("Internal");
        setJobType("Full time");
        setMinYearsExperience(0);
        setPaymentType("Annual");
        setMinCtc(null);
        setMaxCtc(null);
        setMinHourlyRate(null);
        setMaxHourlyRate(null);
        setDescription("");
        setSkillsRequired("");
        setWorkType("On-site");
        setStatus("Draft");
      }
      setError(null);
    }
  }, [open, opportunity]);

  const toDateInput = (value?: string) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      return value;
    }
    return d.toISOString().slice(0, 10);
  };

  const splitSkills = (value: string) =>
    value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const validate = (): string | null => {
    if (!title.trim()) return "Title is required";
    if (!applicationDeadline.trim()) return "Application deadline is required";
    if (!workLocation.trim()) return "Work location is required";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload: Partial<JobOpportunity> = {
      title: title.trim(),
      department: department.trim() || null,
      for_organization_name: forOrganizationName.trim() || null,
      application_deadline: applicationDeadline,
      work_location: workLocation.trim(),
      hiring_type: hiringType,
      job_type: jobType,
      min_years_experience: Number(minYearsExperience) || 0,
      payment_type: paymentType,
      min_ctc: paymentType === "Annual" ? minCtc : null,
      max_ctc: paymentType === "Annual" ? maxCtc : null,
      min_hourly_rate: paymentType === "Hourly" ? minHourlyRate : null,
      max_hourly_rate: paymentType === "Hourly" ? maxHourlyRate : null,
      description: description.trim() || null,
      skills_required: splitSkills(skillsRequired),
      work_type: workType,
      status,
    };

    setLoading(true);
    setError(null);
    try {
      if (isEditing && opportunity?.opportunity_id) {
        await JobOpportunityClient.updateOpportunity(opportunity.opportunity_id, payload);
      } else {
        await JobOpportunityClient.createOpportunity(payload as JobOpportunity);
      }
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || "Failed to save job opportunity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Loader isLoading={loading} message={isEditing ? "Updating job opportunity..." : "Creating job opportunity..."} />
      <Modal
        open={open}
        onClose={() => {
          if (!loading) {
            setError(null);
            onClose();
          }
        }}
        title={isEditing ? "Edit Job Opportunity" : "Create Job Opportunity"}
        width="640px"
      >
        <form onSubmit={handleSubmit} className="jo-modal-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label>
              Title <span className="jo-required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Senior Frontend Developer"
            />
          </div>

          <div className="form-group">
            <label>Department</label>
            <input
              type="text"
              className="form-control"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g. Engineering"
            />
          </div>

          <div className="form-group">
            <label>For Organization</label>
            <input
              type="text"
              className="form-control"
              value={forOrganizationName}
              onChange={(e) => setForOrganizationName(e.target.value)}
              placeholder="e.g. Acme Corp"
            />
          </div>

          <div className="form-group">
            <label>
              Application Deadline <span className="jo-required">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              value={applicationDeadline}
              onChange={(e) => setApplicationDeadline(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>
              Work Location <span className="jo-required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={workLocation}
              onChange={(e) => setWorkLocation(e.target.value)}
              placeholder="e.g. Bangalore, India"
            />
          </div>

          <div className="form-group">
            <label>Hiring Type</label>
            <select value={hiringType} onChange={(e) => setHiringType(e.target.value)}>
              {HiringTypes.map((ht) => (
                <option key={ht} value={ht}>{ht}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Job Type</label>
            <select value={jobType} onChange={(e) => setJobType(e.target.value)}>
              {JobTypes.map((jt) => (
                <option key={jt} value={jt}>{jt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Min Years Experience</label>
            <input
              type="number"
              className="form-control"
              min={0}
              value={minYearsExperience}
              onChange={(e) => setMinYearsExperience(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="form-group">
            <label>Payment Type</label>
            <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              {PaymentTypes.map((pt) => (
                <option key={pt} value={pt}>{pt}</option>
              ))}
            </select>
          </div>

          {paymentType === "Annual" && (
            <div className="form-group" style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <label>Min CTC (Annual)</label>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  value={minCtc ?? ""}
                  onChange={(e) => setMinCtc(e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="Min CTC"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Max CTC (Annual)</label>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  value={maxCtc ?? ""}
                  onChange={(e) => setMaxCtc(e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="Max CTC"
                />
              </div>
            </div>
          )}

          {paymentType === "Hourly" && (
            <div className="form-group" style={{ display: "flex", gap: "10px" }}>
              <div style={{ flex: 1 }}>
                <label>Min Hourly Rate</label>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  value={minHourlyRate ?? ""}
                  onChange={(e) => setMinHourlyRate(e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="Min Rate"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Max Hourly Rate</label>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  value={maxHourlyRate ?? ""}
                  onChange={(e) => setMaxHourlyRate(e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="Max Rate"
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Description</label>
            <RichTextEditor
              value={description}
              onChange={(content) => setDescription(content)}
              placeholder="Job description..."
            />
          </div>

          <div className="form-group">
            <label>Skills Required</label>
            <input
              type="text"
              className="form-control"
              value={skillsRequired}
              onChange={(e) => setSkillsRequired(e.target.value)}
              placeholder="e.g. React, TypeScript, Node.js"
            />
            <span className="jo-form-hint">Comma-separated list of skills.</span>
          </div>

          <div className="form-group">
            <label>Work Type</label>
            <select value={workType} onChange={(e) => setWorkType(e.target.value)}>
              {WorkTypes.map((wt) => (
                <option key={wt} value={wt}>{wt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {Statuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="jo-form-actions">
            <button type="button" className="jo-btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {isEditing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default JobOpportunityFormModal;
